import { countTotalBet, getMinimalBet, rawTotalBet } from '../../../utils/bets';
import { useCoreSelector } from '../../CoreStore';

import type { BetsSliceType, BetType, DragBufferType, ILimit } from './types';

export const useBetSlice = {
  bets: (): BetType | undefined => {
    return useCoreSelector((state) => state.bets.betStack?.[state.bets.betStack.length - 1]);
  },
  lastBet: (): BetsSliceType['lastBet'] => {
    return useCoreSelector((state) => state.bets.lastBet);
  },
  betsReady: (): boolean => {
    return useCoreSelector((state) => !!state.bets.betList);
  },
  betList: (): BetsSliceType['betList'] => {
    return useCoreSelector((state) => state.bets.betList);
  },
  betLimits: (): BetsSliceType['betLimits'] => {
    return useCoreSelector((state) => state.bets.betLimits);
  },
  isLowBalance: (balance?: number): boolean => {
    return useCoreSelector((state) => {
      if (!balance) return true;
      const minBet = getMinimalBet(state.bets.betLimits);
      return balance < minBet;
    });
  },
  isReBetEnabled: (balance: number, digits: number): boolean => {
    return useCoreSelector((state) => {
      // Conditions
      // 1) there is a last bet
      // 2) It is not 0
      // 3) It does not include bet spots that are disabled now
      // 4) It fits in balance
      if (state.bets.lastBet) {
        const savedBet = state.bets.lastBet.betSpots;
        const previousBetSpots = Object.keys(savedBet || {}).filter((el) => (savedBet?.[el]?.value || 0) > 0);
        const betUnavailable =
          previousBetSpots.length === 0 || previousBetSpots.some((r) => state.bets.disabledBetSpots.includes(r));
        const totalBet = countTotalBet(savedBet, digits);
        if (!betUnavailable && totalBet <= balance) {
          return true;
        }
      }
      return false;
    });
  },
  getMinBet: (): number => {
    return useCoreSelector((state) => getMinimalBet(state.bets.betLimits));
  },
  rawTotalBet: (): number => {
    const bets = useBetSlice.bets();
    if (!bets) return 0;
    return rawTotalBet(bets.betSpots);
  },
  getDisabledBetSpots: (): string[] => {
    return useCoreSelector((state) => state.bets.disabledBetSpots);
  },
  totalBet: (): number => {
    const rawTotalBet = useBetSlice.rawTotalBet();
    const overridingTotalBet = useCoreSelector((state) => state.bets.overridingTotalBet);
    return overridingTotalBet ?? rawTotalBet;
  },
  acceptedBets: (): BetsSliceType['acceptedBets'] => {
    return useCoreSelector((state) => state.bets.acceptedBets);
  },
  getDragBuffer: (): DragBufferType | null => {
    return useCoreSelector((state) => (state.bets.dragBuffer?.preliminary ? null : state.bets.dragBuffer));
  },
  getPreliminaryDragBuffer: (): DragBufferType | null => {
    return useCoreSelector((state) => state.bets.dragBuffer);
  },
  getIsDragging: (): boolean => {
    return useCoreSelector((state) => !!state.bets.dragBuffer);
  },
  /**
   * Get timestamp of when was the last time betStack was updated
   */
  getUpdateTimestamp: (): number => {
    return useCoreSelector((state) => state.bets.updateTimestamp);
  },
  getTableLimits: (): ILimit | null => {
    return useCoreSelector((state) => state.bets.tableLimits);
  },
};
